import { downgradeToAJSComponent } from 'ib-web-commons/utils';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    Directive,
    EventEmitter,
    forwardRef,
    Inject, Injectable,
    Input,
    Output, OnDestroy, TemplateRef
} from '@angular/core';
import {
    IbPlusAccordionComponent
} from '@shared/components/ib-plus-accordion/ib-plus-accordion/ib-plus-accordion.component';
import { animate, state, style, transition, trigger } from '@angular/animations';


/**
 * Simple utility to get a not repeated id.
 */
@Injectable({
    providedIn: 'root'
})
class IbPlusAccordionIndexGeneratorService {
    private id = 0;
    getId () {
        return `ib-plus-accordiontab-${this.id++}`;
    }
}

/**
 * Utility directive to declare header inside accordion tab and have autocompletion and not use string in ng template.
 * @example
 * <ib-plus-accordion-tab>
 *     <ng-template ibPlusAccordionTabHeader></ng-template>
 *     <div>Accordion toggleable content</div>
 * </ib-plus-accordion-tab>
 */
@Directive({
    selector: 'ng-template[ibPlusAccordionTabHeader]'
})
export class IbPlusAccordionTabHeaderDirective {}

/**
 * Utility directive to declare lazy-loading content inside accordion tab and have autocompletion and not use string in ng template.
 * Do not use this by default, if not lazy loading is wanted.
 * @example
 * <ib-plus-accordion-tab header="HEADER_KEY">
 *     <ng-template ibPlusAccordionTabContent></ng-template>
 * </ib-plus-accordion-tab>
 */
@Directive({
    selector: 'ng-template[ibPlusAccordionTabContent]'
})
export class IbPlusAccordionTabContentDirective {}

/**
 * Component to every tab inside the IbPlusAccordion Component, can have an accordion tab header and content.
 * @see https://app.zeplin.io/project/5a8ed1c912e091128c5967c5/screen/5db06e054fd65212671f1921
 */
@Component({
    selector: 'ib-plus-accordion-tab',
    templateUrl: './ib-plus-accordion-tab.component.html',
    styleUrls: ['./ib-plus-accordion-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('tabContent', [
            state('hidden', style({ height: '0' })),
            state('visible', style({ height: '*' })),
            transition('visible <=> hidden', [animate('{{transitionParams}}')]),
            transition('void => *', animate(0))
        ])
    ]
})
export class IbPlusAccordionTabComponent implements OnDestroy {

    /**
     * Default header text, it will be translated by default if not specified in translateHeader.
     */
    @Input() header = '';
    /**
     * If translating the header would be necessary, if an object is passed it will be used as translate params.
     * It is false by default.
     */
    @Input() translateHeader: boolean | Record<string, string> = true;
    /**
     * Disable the tab to be in a locked status. False by default.
     */
    @Input() disabled = false;
    /**
     * Output for the bidirectional selected variable.
     */
    @Output() selectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Bidirectional input/output to declare if the tab is selected (opened) and subscribe to changes. False by default.
     */
    @Input() get selected () {
        return this.isSelected;
    }
    set selected (val: boolean) {
        this.isSelected = val;
        if (!this.loaded && this.cache) {
            this.changeDetector.detectChanges();
        }
    }

    /**
     * Internal variable to save the real status (open or not) of the section
     * @private
     */
    private isSelected = false;
    /**
     * If the component has been loaded at least once.
     */
    public loaded = false;
    /**
     * Cache the lazy loaded modules. True by default, this could be a @Input if necessary.
     */
    public cache = true;
    /**
     * Animation options. Iberia Plus defaults.
     */
    public transitionOptions = '400ms cubic-bezier(0.86, 0, 0.07, 1)';
    /**
     * Identifier of the tab component. This will be autogenerated by default and does not maintain id.
     */
    @Input() id = this.indexGeneratorService.getId();
    /**
     * The template header html pass as ng-template with directive to print in the header.
     * @example
     * <ib-plus-accordion-tab>
     *     <ng-template ibPlusAccordionTabHeader></ng-template>
     *     <div>Accordion toggleable content</div>
     * </ib-plus-accordion-tab>
     */
    @ContentChild(IbPlusAccordionTabHeaderDirective, { read: TemplateRef }) headerTemplate: TemplateRef<IbPlusAccordionTabHeaderDirective> | undefined;
    /**
     * The template header html pass as ng-template with directive to print in the content in a lazy loading way.
     * @example
     * <ib-plus-accordion-tab header="HEADER_KEY">
     *     <ng-template ibPlusAccordionTabContent></ng-template>
     * </ib-plus-accordion-tab>
     */
    @ContentChild(IbPlusAccordionTabContentDirective, { read: TemplateRef }) contentTemplate: TemplateRef<IbPlusAccordionTabContentDirective> | undefined;

    /**
     * Parent accordion component that manages all the tabs.
     */
    public accordion: IbPlusAccordionComponent;


    constructor (
        private indexGeneratorService: IbPlusAccordionIndexGeneratorService,
        public changeDetector: ChangeDetectorRef,
        @Inject(forwardRef(() => IbPlusAccordionComponent)) accordion: IbPlusAccordionComponent
    ) {
        this.accordion = accordion;
    }


    /**
     * Function to toggle the collapsable/s affected with the user interaction.
     * @param event Event of the user that provoked the change.
     */
    toggle (event: Event): void {
        if (this.disabled) {
            return;
        }

        const index = this.findTabIndex();

        if (this.selected) {
            this.selected = false;
            this.accordion.closed.emit({ originalEvent: event, index });
        } else {
            if (!this.accordion.multiple) {
                for (const tab of this.accordion.tabs) {
                    if (tab.selected) {
                        tab.selected = false;
                        tab.selectedChange.emit(false);
                        tab.changeDetector.markForCheck();
                    }
                }
            }

            this.selected = true;
            this.loaded = true;
            this.accordion.opened.emit({ originalEvent: event, index });
        }

        this.selectedChange.emit(this.selected);
        this.accordion.updateActiveIndex();
        this.changeDetector.markForCheck();

        event.preventDefault();
    }

    /**
     * Find the index of this tab to change the status.
     */
    findTabIndex () {
        let index = -1;
        for (let i = 0; i < this.accordion.tabs.length; i++) {
            if (this.accordion.tabs[i] === this) {
                index = i;
                break;
            }
        }
        return index;
    }

    /**
     * Function to change the user interaction and allow the accessibility open and close with keyboard.
     * @param event
     */
    onKeydown (event: KeyboardEvent) {
        if (event.code === 'Space' || event.code === 'Enter') {
            this.toggle(event);
            event.preventDefault();
        }
    }
    /**
     * If this component unmount clean and update the accordion component.
     */
    ngOnDestroy () {
        this.accordion.tabs.splice(this.findTabIndex(), 1);
    }
}


downgradeToAJSComponent(
    'ibPlusAccordionTab',
    {
        component: IbPlusAccordionTabComponent,
        inputs: [
            'header',
            'translateHeader',
            'disabled',
            'selected'
        ]
    },
    'ib-shared-components-module'
);
